import * as React from 'react'
import { XIcon } from 'lucide-react'
import { cn } from '../../lib/utils'
import { Button } from './button'
import { Dialog, DialogClose, DialogContent, DialogTitle } from './dialog'

export interface FormDialogProps {
	open:           boolean
	onOpenChange:   (open: boolean) => void
	title:          React.ReactNode
	children:       React.ReactNode
	/** Submit handler — typically `form.handleSubmit(onSubmit)` from react-hook-form. */
	onSubmit?:      (e: React.FormEvent<HTMLFormElement>) => void
	submitLabel:    string
	cancelLabel:    string
	closeAriaLabel?: string
	isSubmitting?:   boolean
	isSubmitDisabled?: boolean
	/** Override DialogContent className (size, paddings). Default: `sm:max-w-[30rem]`. */
	contentClassName?: string
}

function FormDialog({
	open, onOpenChange, title, children, onSubmit,
	submitLabel, cancelLabel, closeAriaLabel = 'Close',
	isSubmitting, isSubmitDisabled, contentClassName,
}: FormDialogProps) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange} disablePointerDismissal>
			<DialogContent
				showCloseButton={false}
				className={cn('sm:max-w-[30rem] w-full p-0 gap-0 rounded-lg overflow-hidden', contentClassName)}
			>
				<div className="flex items-center justify-between border-b border-(--color-border) bg-(--color-background) p-6">
					<DialogTitle className="text-2xl leading-8 tracking-[-0.6px] font-[500] text-(--color-foreground)">
						{title}
					</DialogTitle>
					<DialogClose
						render={
							<button
								type="button"
								className="rounded-(--radius-sm) p-0.5 text-(--color-muted-foreground) hover:text-(--color-foreground) transition-colors"
							/>
						}
					>
						<XIcon className="size-4" />
						<span className="sr-only">{closeAriaLabel}</span>
					</DialogClose>
				</div>

				<form onSubmit={onSubmit} className="min-w-0 overflow-hidden">
					<div className="flex min-w-0 flex-col gap-4 px-6 pt-4 pb-6">
						{children}
					</div>

					<div className="flex items-center justify-end gap-2 border-t border-(--color-border) px-6 py-4">
						<Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
							{cancelLabel}
						</Button>
						<Button type="submit" disabled={isSubmitting || isSubmitDisabled}>
							{submitLabel}
						</Button>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	)
}

FormDialog.displayName = 'FormDialog'

export { FormDialog }
